import { sendOperationCommand, audioPresetPlay } from "./sendData";
import operatorApiService from "../services/operator-api.service";

export interface YmcConfig {
  clientId: string;
  clientSecret: string;
  redirectUri: string;
  authServerUrl: string;
}

export interface YmcTokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

export interface VehicleDlyInfo {
  index: number; // 配列順序管理用インデックス(0-3)
  last_received_time: number;
  delay_time: number;
  last_record_id: number | null; // 前回取得したID
}

// グローバル変数
let ymcToken: string | null = null;
let refreshToken: string | null = null;
let monitoringIntervalId: number | null = null;
let refreshTokenIntervalId: number | null = null;
let vehicleDataUpdateIntervalId: number | null = null;
let sendDataCallback: ((data: any) => void) | null = null;
let observeCC: string = "";
let monitorConsoleId: number | null = null;
let iYmcPutFailedCount: number = 0;
let gVehicleCommunicationCodes: any[] = [];
let vehicleDlyData: VehicleDlyInfo[] = [];
let announceTime: number[] = [0, 0, 0, 0];
let ymcStatusUpdateCallbacks: Map<string, (status: any) => void> = new Map();
let modalCallback: ((message: string) => void) | null = null;

// Authorizationヘッダ用Basic64エンコード
const encoded = btoa(
  "6r98hha0m3efolshk1hp6bicjm:1sdd6edosv240crts9abgqegfdjr3c7ao99t4o5umfjgch99a4vj"
);

// デフォルト設定
const defaultYmcConfig: YmcConfig = {
  clientId: "6r98hha0m3efolshk1hp6bicjm",
  clientSecret: "1sdd6edosv240crts9abgqegfdjr3c7ao99t4o5umfjgch99a4vj",
  redirectUri: "https://moscat.net/observe",
  authServerUrl: "https://auth.demo-ppm.com",
};

// VEHICLE_CTRL_ID定数
const VEHICLE_CTRL_ID = {
  CAR_NO_8: "FAwsZYYtBXNMJ78sUkiLY5",
  CAR_NO_9: "QXBwgRJjDhhi9KE7Zi4qTC",
  CAR_NO_10: "2QtGWfAzcdPuP8uGeA6wV9",
  CAR_NO_11: "JY4vUHBxhbp4sUeNacxWMn",
} as const;

// アナウンス用定数
const PRESET_PRIORITY_MID = 2;
const PRESET_PRIORITY_LOW = 3;
const PRESET_VOLUME_L = 2;
const UI_4CAR_MODE = 4;

// YMC管制初期化
export function shouldRedirectToAuth(): boolean {
  const params = new URLSearchParams(window.location.search);
  return !params.get("code");
}

export function redirectToAuth(): void {
  // 100ms間の同期ブロッキング
  const startMsec = new Date().getTime();
  const endTime = startMsec + 100;
  while (new Date().getTime() < endTime) {
    // 処理をブロック
  }

  window.location.href = `${defaultYmcConfig.authServerUrl}/oauth2/authorize?response_type=code&client_id=${defaultYmcConfig.clientId}&redirect_uri=${defaultYmcConfig.redirectUri}`;
  console.log("moscat-observe Page Redirected");
}

// ------------------------------
// --- YMC管制サーバー（ControlCenter）との機能連携
// ------------------------------
export function getYmcAccessToken(): void {
  // 認可コードを取得
  const params = new URLSearchParams(window.location.search);
  const authCode = params.get("code") ?? "";

  // アクセストークン／リフレッシュトークンの取得()
  fetch(`${defaultYmcConfig.authServerUrl}/oauth2/token`, {
    method: "POST",
    headers: {
      Authorization: "Basic " + encoded,
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body: new URLSearchParams({
      grant_type: "authorization_code",
      client_id: defaultYmcConfig.clientId,
      code: authCode,
      redirect_uri: defaultYmcConfig.redirectUri,
    }),
  })
    .then((response) => response.json())
    .then((data) => {
      ymcToken = data.access_token;
      refreshToken = data.refresh_token;
      console.info("GET Token:");
      console.log("POST oauth2/token success. token:" + ymcToken);

      // 認証後にURLからcodeパラメータを消す
      if (window && window.history && window.location) {
        const url = new URL(window.location.href);
        url.searchParams.delete("code");
        window.history.replaceState(
          {},
          document.title,
          url.pathname + url.search
        );
      }
    })
    .catch(() => {
      console.error("YMC POST oauth2/token Failed");
      console.error("POST oauth2/token Failed.");
      // 認証失敗時はYMCログイン画面へリダイレクト
      redirectToAuth();
    });

  startMonitoringInfoExchange();
  startVehicleDlyDataUpdate();
  startTokenRefresh();
}

// 200ms周期で、管制サーバー←→監視センター間の情報を更新
// YMC連携停止中
function startMonitoringInfoExchange(): void {
  monitoringIntervalId = window.setInterval(() => {
    exchangeMonitoringInfo();
  }, 200);
}

// 1000ms周期で、DBからvehicle_info_modemを取得
function startVehicleDlyDataUpdate(): void {
  // 初回実行
  updateVehicleDlyData();

  vehicleDataUpdateIntervalId = window.setInterval(() => {
    updateVehicleDlyData();
  }, 1000);
}

// 60分周期で、管制サーバーのアクセスTokenをRefleshする
function startTokenRefresh(): void {
  refreshTokenIntervalId = window.setInterval(() => {
    reflashYmcAccessToken();
  }, 3600000); // 60分
}

// リフレッシュ・トークン
function reflashYmcAccessToken(): void {
  if (!refreshToken) {
    console.error("リフレッシュトークンがありません");
    return;
  }

  // アクセストークン／リフレッシュトークンの取得()
  fetch(`${defaultYmcConfig.authServerUrl}/oauth2/token`, {
    method: "POST",
    headers: {
      Authorization: "Basic " + encoded,
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body: new URLSearchParams({
      grant_type: "refresh_token",
      client_id: defaultYmcConfig.clientId,
      refresh_token: refreshToken,
    }),
  })
    .then((response) => response.json())
    .then((data) => {
      ymcToken = data.access_token;
      console.info("Reflesh Token:" + data.access_token);
    })
    .catch(() => {
      console.error("YMC POST oauth2/token Failed");
      // 認証失敗時はYMCログイン画面へリダイレクト
      redirectToAuth();
    });

  // ログ代わりに、操作をTextroomに送信
  if (sendDataCallback) {
    sendDataCallback(
      sendOperationCommand(observeCC, observeCC, "reflashYmcAccessToken")
    );
  }
}

// 車両dly情報を更新
async function updateVehicleDlyData(): Promise<void> {
  try {
    const responseData = await operatorApiService.doGetVehicleDlyInfo({
      monitorConsoleId: monitorConsoleId,
    });
    const newVehicleDlyData: VehicleDlyInfo[] = [];

    for (let i = 0; i < 4; i++) {
      if (i < gVehicleCommunicationCodes.length) {
        const gvehicleCommunicationCode =
          gVehicleCommunicationCodes[i]?.vehicle_communication_code;

        // APIレスポンスからvehicle_communication_codeが一致する車両を検索
        const vehicleData = (responseData.vehicles || []).find(
          (v: any) => v.vehicle_communication_code === gvehicleCommunicationCode
        );

        // 前回のデータを取得
        const prevData = vehicleDlyData.find((v) => v.index === i);
        const prevRecordId = prevData?.last_record_id || null;

        if (vehicleData) {
          // DBデータが存在する場合
          const currentRecordId = vehicleData.vehicle_info_modem_id;
          const delayTime = vehicleData.delay_time || 0;
          let lastReceivedTime = prevData?.last_received_time || 0;

          // レコードIDが前回と異なる場合、最終受信時間を更新
          if (currentRecordId !== prevRecordId) {
            lastReceivedTime = new Date().getTime();
          }

          newVehicleDlyData.push({
            index: i,
            last_received_time: lastReceivedTime,
            delay_time: delayTime,
            last_record_id: currentRecordId,
          });
        } else {
          // DBデータが存在しない場合はデフォルト値
          newVehicleDlyData.push({
            index: i,
            last_received_time: 0,
            delay_time: 0,
            last_record_id: null,
          });
        }
      } else {
        newVehicleDlyData.push({
          index: i,
          last_received_time: 0,
          delay_time: 0,
          last_record_id: null,
        });
      }
    }
    vehicleDlyData = newVehicleDlyData;
  } catch (error) {
    // エラー時はデフォルト値を設定
    vehicleDlyData = [
      {
        index: 0,
        last_received_time: 0,
        delay_time: 0,
        last_record_id: null,
      },
      {
        index: 1,
        last_received_time: 0,
        delay_time: 0,
        last_record_id: null,
      },
      {
        index: 2,
        last_received_time: 0,
        delay_time: 0,
        last_record_id: null,
      },
      {
        index: 3,
        last_received_time: 0,
        delay_time: 0,
        last_record_id: null,
      },
    ];
  }
}

// 遠隔監視情報共有
export function exchangeMonitoringInfo(): void {
  if (!ymcToken) {
    console.warn("YMC access token not available");
    return;
  }

  const aliveVal = [2, 2, 2, 2]; // 各車のアライブ情報の格納
  const currentTime = new Date().getTime();

  for (const vdData of vehicleDlyData) {
    const index = vdData.index;
    if (index >= 0 && index < 4) {
      let status = 2;

      // last_received_timeが0の場合（初期状態・データなし）は不良と判定
      if (vdData.last_received_time > 0) {
        // Modem_Info受信間隔が2000ms以下であることをチェック
        // 2000ms以上受信が無ければ映像遅延とaliveVal=2と判定する
        if (currentTime - vdData.last_received_time <= 2000) {
          // Modem_Infoのdly値チェック
          if (vdData.delay_time <= 1000) {
            status = 0; // 良好
          } else if (vdData.delay_time <= 2000) {
            status = 1; // 可
          }
          // 2000ms超過の場合：status = 2　不良のまま
        }
      }
      aliveVal[index] = status;
    }
  }

  // 車両ID、監視映像のアライブ情報(0:良好,1:可,2:不良)、監視映像の遅延時間(暫定値)をパラメータにセット
  const monitoringData = {
    vehicles: [
      {
        vehicle_id: VEHICLE_CTRL_ID.CAR_NO_8,
        alive_monitor: aliveVal[0],
        delay_monitor: null,
      },
      {
        vehicle_id: VEHICLE_CTRL_ID.CAR_NO_9,
        alive_monitor: aliveVal[1],
        delay_monitor: null,
      },
      {
        vehicle_id: VEHICLE_CTRL_ID.CAR_NO_10,
        alive_monitor: aliveVal[2],
        delay_monitor: null,
      },
      {
        vehicle_id: VEHICLE_CTRL_ID.CAR_NO_11,
        alive_monitor: aliveVal[3],
        delay_monitor: null,
      },
    ],
  };

  // PUT Monitoring実行
  fetch("https://gsm-control.demo-ppm.com/v1/monitoring", {
    method: "PUT",
    headers: {
      Authorization: ymcToken || "",
      "Content-Type": "application/json;charset=utf-8",
    },
    body: JSON.stringify(monitoringData),
  })
    .then((response: Response) => {
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      return response.json();
    })
    .then((data: any) => {
      // 通信が成功した場合の処理
      iYmcPutFailedCount = 0; // 成功時は失敗カウントをリセット
      console.log("YmcData", data);

      // 車両状態をボタンに反映する
      reflectYmcStatus(data);

      // アナウンス再生要求確認
      checkAnnounce(data);
    })
    .catch((error: any) => {
      // 通信が失敗した場合の処理
      console.log(
        "YMC PUT Monitoring Err XMLHttpRequest : " + (error.status || "unknown")
      );
      console.log("YMC textStatus     : " + (error.message || "error"));
      console.log("YMC errorThrown    : " + (error.message || ""));

      if (sendDataCallback) {
        sendDataCallback(
          sendOperationCommand(
            observeCC,
            observeCC,
            "PUT Monitoring Error " + (error.message || "")
          )
        );
      }

      if (iYmcPutFailedCount == 3) {
        console.log("YMC iYmcPutFailedCount==3 ReConnect YMC");

        // アクセストークンをリフレッシュで再取得()
        fetch(`${defaultYmcConfig.authServerUrl}/oauth2/token`, {
          method: "POST",
          headers: {
            Authorization: "Basic " + encoded,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: new URLSearchParams({
            grant_type: "refresh_token",
            client_id: defaultYmcConfig.clientId,
            refresh_token: refreshToken || "",
          }),
        })
          .then((response) => response.json())
          .then((data) => {
            ymcToken = data.access_token;
            console.info("Reflesh Token:" + ymcToken);
            const ope = "Reflesh Token success.";
            if (sendDataCallback) {
              sendDataCallback(sendOperationCommand(observeCC, observeCC, ope));
            }
          })
          .catch(() => {
            console.log("YMC POST oauth2/token Failed");
            const ope = "Reflesh Token Failed.";
            if (sendDataCallback) {
              sendDataCallback(sendOperationCommand(observeCC, observeCC, ope));
            }

            // 認可エンドポイントのリクエスト(管制ログイン)からやり直し
            window.alert(
              "管制サーバーとの接続が切れました。再ログインして下さい。"
            );
          });
      }
      iYmcPutFailedCount++;
    });
}

// 管制からの車両情報をボタンに状態反映する
function reflectYmcStatus(data: any): void {
  if (data?.vehicles) {
    data.vehicles.forEach((vehicle: any, index: number) => {
      if (vehicle) {
        // 車両通信コードを取得
        const vehicleCommunicationCode =
          getVehicleCommunicationCodeByIndex(index);
        if (!vehicleCommunicationCode) {
          return;
        }

        // YMCサーバーからのデータをそのまま渡す
        const callback = ymcStatusUpdateCallbacks.get(vehicleCommunicationCode);
        if (callback) {
          callback(vehicle);
        }
      }
    });
  }
}

// 管制サーバーからのアナウンス再生要求をチェックし、要求があれば電文を発行する
function checkAnnounce(data: any): void {
  // 車両毎にアナウンス再生要求があるかチェックする
  for (let idx = 0; idx < UI_4CAR_MODE; idx++) {
    if (!data.vehicles || !data.vehicles[idx]) {
      continue;
    }

    const vehicle = data.vehicles[idx];
    if (
      !vehicle.announce_request ||
      vehicle.announce_request === "" ||
      vehicle.announce_time === 0
    ) {
      // 再生要求なし
      continue;
    }

    // アナウンス再生要求の発生時刻を確認（前回値と同じなら捨てる）
    if (announceTime[idx] === vehicle.announce_time) {
      // アナウンス要求は前回で受信処理済み
      console.log("*DebugYMC checkAnnounce() time is same. Ignore");
      continue;
    }

    // アナウンス要求受信時刻を保存
    announceTime[idx] = vehicle.announce_time;

    // 対応する車両の通信コードを取得
    const vehicleCommunicationCode = getVehicleCommunicationCodeByIndex(idx);
    if (!vehicleCommunicationCode) {
      console.warn(`Vehicle communication code not found for index ${idx}`);
      continue;
    }

    // アナウンス再生要求(string型)に応じて、電文を発行
    let presetNo = 0;
    switch (vehicle.announce_request) {
      // case 'before-arrival':   仕様定義が無いのでコメントアウト
      //     // 停留所到着前
      //     presetNo = 0;
      //     break;
      case "arrival":
        // 停留所に到着
        if (vehicle.stop_position_name === "志比降車場") {
          presetNo = 7;
        } else if (vehicle.stop_position_name === "荒谷降車場") {
          presetNo = 8;
        }
        break;
      case "wait-pass":
        // すれ違いのために待避所に停車
        presetNo = 4;
        break;
      // case "limit-speed":
      //   // 調停による速度制限中
      //   presetNo = 24;
      //   break;
      // case "change-mode-remote":
      //   // 遠隔操縦モードへ切替
      //   presetNo = 25;
      //   break;
      // case "change-mode-auto":
      //   // 自動運転モードへ切替
      //   presetNo = 26;
      //   break;
      case "emergency":
        // 非常停止
        presetNo = 6;
        break;
      // case 'recovery':
      //     // 非常停止からの復帰 →2023/02/15 永平寺結合試験で鳴らさない仕様に変更
      //     presetNo = 3;
      //     break;
      default:
        break;
    }

    // Preset指示がDefault値(０)ならアナウンス再生指示を出さない
    if (presetNo === 0) {
      continue;
    }

    // 車内ワンショット再生
    const sr = observeCC;
    const dt = vehicleCommunicationCode;
    const voicebtn = 1; // 車内に固定
    const announcementCount = 1; // ワンショット

    let pri = PRESET_PRIORITY_LOW;
    if (presetNo === 2 || presetNo === 7 || presetNo === 8 || presetNo === 9) {
      // 発進アナウンスは、優先度：中
      pri = PRESET_PRIORITY_MID;
    }
    const vol = PRESET_VOLUME_L; // 再生音量：Default

    if (sendDataCallback) {
      sendDataCallback(
        audioPresetPlay(sr, dt, presetNo, pri, vol, voicebtn, announcementCount)
      );
    }

    // 関数が用意されていないため、コメントアウトで対応。
    // audioPresetPlayDB({{ config('const.FLAG_OFF') }}, null);

    // ログ用操作コマンド送信
    const ope = "PlayPresetAudioFromYMC" + String(presetNo);
    if (sendDataCallback) {
      sendDataCallback(sendOperationCommand(sr, dt, ope));
    }

    console.log("YMC checkAnnounce() presetNo:" + presetNo);
    console.log("YMC checkAnnounce() time:" + vehicle.announce_time);
  }
}

// インデックスに対応する車両通信コードを取得
function getVehicleCommunicationCodeByIndex(index: number): string | null {
  if (index >= 0 && index < gVehicleCommunicationCodes.length) {
    return (
      gVehicleCommunicationCodes[index]?.vehicle_communication_code || null
    );
  }
  return null;
}

// sendData関数のコールバックを設定
export function setSendDataCallback(
  callback: (data: any) => void,
  cc: string
): void {
  sendDataCallback = callback;
  observeCC = cc;
}

// gVehicleCommunicationCodesを設定
export function setVehicleCommunicationCodes(vehicleCodes: any[]): void {
  gVehicleCommunicationCodes = vehicleCodes;
}

// monitorConsoleIdを設定
export function setMCId(mCId: number): void {
  monitorConsoleId = mCId;
}

// YMC状態更新コールバックを設定（車両別）
export function setYmcStatusUpdateCallback(
  vehicleCode: string,
  callback: (status: any) => void
): void {
  ymcStatusUpdateCallbacks.set(vehicleCode, callback);
}

// YMC状態更新コールバックを削除（車両別）
export function removeYmcStatusUpdateCallback(vehicleCode: string): void {
  ymcStatusUpdateCallbacks.delete(vehicleCode);
}

// モーダル表示コールバック
export function setModalCallback(callback: (message: string) => void): void {
  modalCallback = callback;
}

// 各車両指示機能
export function sendYmcCommand(commandType: string, vehicleId: number): void {
  const jsonData = {
    command_type: commandType,
  };
  ajaxActionYmc(jsonData, vehicleId);
}

// 全車緊急停止機能
export function sendYmcEmergencyStopAll(): void {
  const jsonData = {
    command_type: "emergency-stop-all",
  };
  ajaxActionYmcAll(jsonData);
}

// 全車運用外機能
export function sendYmcOutOfServiceAll(): void {
  const jsonData = {
    command_type: "out-of-service-all",
  };
  ajaxActionYmcAll(jsonData);
}

/** YMCサーバ連携  選択車両向けコマンド  共通ajax **/
function ajaxActionYmc(jsonData: any, vehicleId: number): void {
  // 選択中の車両ID（永平寺と完全同じURL生成）
  let url: string;
  if (vehicleId === 1) {
    // 8号車
    url =
      "https://gsm-control.demo-ppm.com/v1/vehicles/" +
      VEHICLE_CTRL_ID.CAR_NO_8 +
      "/command";
  } else if (vehicleId === 2) {
    // 9号車
    url =
      "https://gsm-control.demo-ppm.com/v1/vehicles/" +
      VEHICLE_CTRL_ID.CAR_NO_9 +
      "/command";
  } else if (vehicleId === 3) {
    // 10号車
    url =
      "https://gsm-control.demo-ppm.com/v1/vehicles/" +
      VEHICLE_CTRL_ID.CAR_NO_10 +
      "/command";
  } else {
    // 11号車
    url =
      "https://gsm-control.demo-ppm.com/v1/vehicles/" +
      VEHICLE_CTRL_ID.CAR_NO_11 +
      "/command";
  }

  const sr = observeCC;
  const dt = "0001010" + String(Number(vehicleId));
  let ope = "vehicleId:" + vehicleId + "  command:" + JSON.stringify(jsonData);

  fetch(url, {
    method: "POST",
    headers: {
      Authorization: ymcToken || "",
      "Content-Type": "application/json;charset=utf-8",
    },
    body: JSON.stringify(jsonData),
  })
    .then((response: Response) => {
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      return response.json();
    })
    .then((responseData: any) => {
      console.log(
        "****DEBUG POST start-driving success. resCode:" + responseData.code
      );
      ope = ope + " success";
    })
    .catch((error: any) => {
      // 通信が失敗した場合の処理
      console.error("***DEBUG** XMLHttpRequest : " + (error.status || ""));
      console.error("***DEBUG** textStatus     : " + (error.message || ""));
      console.error("***DEBUG** errorThrown    : " + (error || ""));

      if (modalCallback) {
        modalCallback("POST YMC-server error");
      }

      ope = ope + " failed";
    });

  // ログ代わりに、操作をTextroomに送信
  if (sendDataCallback) {
    sendDataCallback(sendOperationCommand(sr, dt, ope));
  }
}

/** YMCサーバ連携 全車向けコマンド 共通ajax **/
function ajaxActionYmcAll(jsonData: any): void {
  const url = "https://gsm-control.demo-ppm.com/v1/vehicles/command";

  fetch(url, {
    method: "POST",
    headers: {
      Authorization: ymcToken || "",
      "Content-Type": "application/json;charset=utf-8",
    },
    body: JSON.stringify(jsonData),
  })
    .then((response: Response) => {
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      return response.json();
    })
    .then((responseData: any) => {
      console.log(
        "****DEBUG POST All-vehicle command success. resCode:" +
          responseData.code
      );
    })
    .catch((error: any) => {
      // 通信が失敗した場合の処理
      console.error("XMLHttpRequest : " + (error.status || ""));
      console.error("textStatus     : " + (error.message || ""));
      console.error("errorThrown    : " + (error || ""));

      if (modalCallback) {
        modalCallback("POST YMC All-command error");
      }
    });

  // ログ代わりに、操作をTextroomに送信
  if (sendDataCallback) {
    const sr = observeCC;
    const dt = observeCC;
    sendDataCallback(sendOperationCommand(sr, dt, jsonData));
  }
}

// クリーンアップ処理
export function ymcCleanup(): void {
  if (monitoringIntervalId) {
    clearInterval(monitoringIntervalId);
    monitoringIntervalId = null;
  }
  if (refreshTokenIntervalId) {
    clearInterval(refreshTokenIntervalId);
    refreshTokenIntervalId = null;
  }
  if (vehicleDataUpdateIntervalId) {
    clearInterval(vehicleDataUpdateIntervalId);
    vehicleDataUpdateIntervalId = null;
  }
}
